import { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const useWebSocketAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [connected, setConnected] = useState(false);
  const [mode, setMode] = useState('connecting');
  const wsRef = useRef(null);
  const pollRef = useRef(null);
  const modeRef = useRef('connecting');

  // Keep modeRef in sync
  useEffect(() => { modeRef.current = mode; }, [mode]);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/securesphere/realtime/alerts/poll`, { credentials: 'include' });
        if (res.ok && !cancelled) {
          const data = await res.json();
          if (data.events) setAlerts(prev => [...data.events, ...prev].slice(0, 50));
          if (data.metrics) setMetrics(data.metrics);
        }
      } catch (error) { console.error('Alert poll failed:', error.message); }
    };

    const startPolling = () => {
      if (pollRef.current || cancelled) return;
      setMode('polling');
      setConnected(true);
      poll();
      pollRef.current = setInterval(poll, 10000);
    };

    const connectWs = () => {
      const wsUrl = API_URL.replace(/^https/, 'wss').replace(/^http/, 'ws') + '/ws/alerts';
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        const timeout = setTimeout(() => {
          if (ws.readyState !== WebSocket.OPEN) { ws.close(); startPolling(); }
        }, 5000);

        ws.onopen = () => {
          clearTimeout(timeout);
          if (!cancelled) { setConnected(true); setMode('ws'); }
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.event === 'threat_alert') setAlerts(prev => [data, ...prev].slice(0, 50));
            else if (data.event === 'metrics_update') setMetrics(data);
          } catch (error) { console.error('WS message parse error:', error.message); }
        };

        ws.onclose = () => {
          clearTimeout(timeout);
          wsRef.current = null;
          if (!cancelled && modeRef.current !== 'polling') startPolling();
        };

        ws.onerror = () => { clearTimeout(timeout); ws.close(); };
      } catch (error) {
        console.error('WebSocket connect failed:', error.message);
        startPolling();
      }
    };

    connectWs();

    return () => {
      cancelled = true;
      if (wsRef.current) wsRef.current.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []); // Empty deps — runs once on mount, cleanup on unmount

  const acknowledge = useCallback((alertId) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: 'acknowledge', alert_id: alertId }));
    }
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  const dismissAll = useCallback(() => { setAlerts([]); }, []);

  return { alerts, metrics, connected, mode, acknowledge, dismissAll, unreadCount: alerts.length };
};

export default useWebSocketAlerts;
